// tslint:disable: no-shadowed-variable

import styled from '@emotion/styled/macro'
import AppCommandBar from 'app/AppCommandBar'
import { AutoStack, dateTimeToGraphQLDateTime, FlexSpinner } from 'components'
import { WIZARD_NAVIGATION } from 'components/FormFooterWizard/FormFooterWizard'
import { useMailChangesQuery } from 'components/Mailbox/Mailbox.generated'
import { MailboxForm, MailboxFormMode } from 'components/Mailbox/MailboxForm'
import { RichTextViewer } from 'components/RichTextViewer/RichTextViewer'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { MailingDialog } from 'dialogs/MailingDialog'
import { FormikActions } from 'formik'
import {
  ProvingDetailsAccomodationForm,
  ProvingDetailsAccomodationFormSchema,
  ProvingDetailsFerryForm,
  ProvingDetailsFerryFormSchema,
  ProvingDetailsFlightForm,
  ProvingDetailsFlightFormSchema,
  ProvingDetailsOrderIdForm,
  ProvingDetailsParticipantForm,
  ProvingDetailsParticipantFormSchema,
  ProvingDetailsParticipantFormValues,
  ProvingDetailsParticipantReassignForm,
  ProvingDetailsParticipantsUnknownForm,
  ProvingDetailsParticipantsUnknownFormValues,
  ProvingDetailsRentalCarForm,
  ProvingDetailsRentalCarFormSchema,
  ProvingDetailsTimespanForm,
  ProvingDetailsTimespanFormSchema,
} from 'forms/ProvingDetailsForm'
import {
  ParticipantDetailsForm,
  ParticipantDetailsFormSchema,
} from 'forms/ProvingDetailsForm/participants/ParticipantDetailsForm'
import { ParticipantHeadOfProvingForm } from 'forms/ProvingDetailsForm/participants/ParticipantHeadOfProvingForm'
import {
  ParticipantsByProvingForm,
  ParticipantsByProvingFormSchema,
} from 'forms/ProvingDetailsForm/participants/ParticipantsByProvingForm'
import {
  ProvingDetailsManuellParticipantForm,
  ProvingDetailsManuellParticipantFormValues,
} from 'forms/ProvingDetailsForm/participants/ProvingDetailsManuellParticipantForm'
import { ProvingDetailsNordschleifeForm } from 'forms/ProvingDetailsForm/participants/ProvingDetailsNordschleifeForm'
import {
  ProvingDetailsSupplierForm,
  ProvingDetailsSupplierFormSchema,
  ProvingDetailsSupplierFormValues,
} from 'forms/ProvingDetailsForm/participants/ProvingDetailsSupplierForm'
import {
  ProvingDetailsTrackHotelForm,
  ProvingDetailsTrackHotelFormSchema,
} from 'forms/ProvingDetailsForm/participants/ProvingDetailsTrackHotelForm'
import { ProvingDetailsTrackHotelOutwardStatusForm } from 'forms/ProvingDetailsForm/participants/ProvingDetailsTrackHotelOutwardStatusForm'
import { ProvingDetailsTrackHotelReturnStatusForm } from 'forms/ProvingDetailsForm/participants/ProvingDetailsTrackHotelReturnStatusForm'
import {
  ProvingDetailsWorkerForm,
  ProvingDetailsWorkerFormSchema,
  ProvingDetailsWorkerFormValues,
} from 'forms/ProvingDetailsForm/participants/ProvingDetailsWorkerForm'
import { ProvingDetailsAnnotationForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsAnnotationForm'
import { ProvingDetailsGroupForm } from 'forms/ProvingDetailsForm/shared/ProvingDetailsGroupForm'
import { Flex } from 'grid'
import {
  canUpdateProving,
  clearObjectIdsByPath,
  isInProvingRole,
  omitDeep,
  removeNullProperties,
} from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import {
  useCommand,
  useDeselectCommand,
  useGroupCommand,
} from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useSelection } from 'hooks/useSelection'
import { isEqual, omit, startsWith, times, uniqBy } from 'lodash'
import {
  AccomodationType,
  BookingStatus,
  CarType,
  FlightType,
  MailTemplateType,
  OrderIdDistribution,
  Participant,
  ParticipantBatchInput,
  ParticipantInsertInput,
  ParticipantsAddInput,
  ParticipantUpdateInput,
  PersonType,
  ProvingUserRole,
  ResourceSelectionType,
  TrackHotel,
  User,
  UserRole,
} from 'model'
import {
  ConstrainMode,
  IconButton,
  PanelType,
  Persona,
  PersonaInitialsColor,
  PersonaSize,
  Slider,
  Stack,
} from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import { FormikPanelWizard } from 'panels/FormikWizardPanel'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAddParticipantsMutation,
  useAddResourceRequirementMutation,
  useDataQuery,
  useDeleteParticipantsMutation,
  useUpdateParticipantsMutation,
  useUpdateProvingMutation,
} from './hooks/index'
import { Page, Section } from './Layout'
import { ParticipantList } from './lists'

enum FormType {
  default,
  addParticipant,
  timespan,
  orderId,
  flight,
  flightStatus,
  accomodation,
  accomodationStatus,
  rentalCar,
  rentalCarStatus,
  hotels,
  mailing,
  ferry,
  ferryStatus,
  nordschleife,
  hotelsOutwardStatus,
  hotelsReturnStatus,
  headOfProving,
  annotation,
  details,
}
export enum WizardState {
  none,
  addParticipant,
  /*Uncomment for timespan panel in wizard  */
  timespan,
  group,
  orderId,
  accomodation,
  rentalCar,
  flight,
  hotels,
  nordschleife,
  ferry,
}

export const getActiveWizard = (
  wizardStateArray: { state: WizardState; active: boolean }[]
) => {
  return wizardStateArray.findIndex((element) => element.active === true)
}
const getActiveWizardState = (
  wizardStateArray: { state: WizardState; active: boolean }[]
) => {
  return wizardStateArray[getActiveWizard(wizardStateArray)].state
}

const List = styled(ParticipantList)`
  .ms-DetailsRow-cell:first-child,
  .ms-DetailsHeader-cell:first-child {
    position: sticky;
    inset: 0;
    background-color: inherit;
  }
  .ms-DetailsRow-cellCheck {
    margin-top: 0px;
  }
`

export const PageParticipants: React.FC = () => {
  // #region General hooks
  const [proving, refetch] = useCurrentProving()
  const participants = proving.participants || []
  const {
    data: mailChangesData,
    loading: mailChangesLoading,
    refetch: mailChangesRefetch,
  } = useMailChangesQuery({
    variables: {
      provingId: proving._id,
      participants: participants ? participants.map((i: any) => i._id) : [],
      paths: ['.*'],
    },
  })
  const changes =
    mailChangesData && mailChangesData.mailChanges
      ? mailChangesData.mailChanges
      : []
  const { t } = useTranslation()
  const [selectedItemId, setSelectedItemId] = React.useState()

  const initWizardStates = (isWorkerStates: boolean) => {
    const currentProvingWizardState = [
      { state: WizardState.none, active: true },
      { state: WizardState.addParticipant, active: false },
      // Uncomment for timespan panel in wizard
    ]
    if (proving.enableGroups) {
      currentProvingWizardState.push({
        state: WizardState.group,
        active: false,
      })
    }
    currentProvingWizardState.push({
      state: WizardState.timespan,
      active: false,
    })
    if (!isWorkerStates) {
      currentProvingWizardState.push({
        state: WizardState.orderId,
        active: false,
      })
    }
    currentProvingWizardState.push({
      state: WizardState.accomodation,
      active: false,
    })
    currentProvingWizardState.push({
      state: WizardState.rentalCar,
      active: false,
    })

    if (proving.provingGround && !proving.provingGround.hideFlight) {
      currentProvingWizardState.push({
        state: WizardState.flight,
        active: false,
      })
    }
    if (proving.provingGround && !proving.provingGround.hideHotels) {
      currentProvingWizardState.push({
        state: WizardState.hotels,
        active: false,
      })
    }
    // if (isWorkerStates) {
    //   currentProvingWizardState.splice(2, 1)
    // }
    return currentProvingWizardState
  }
  const [participantWizardState, setParticipantWizardState] = React.useState<
    { state: WizardState; active: boolean }[]
  >(initWizardStates(false))
  const [selection, selectedItems] = useSelection<Participant>({
    getKey: (item) => item._id,
  })

  const resetWizardState = () => {
    const currentIndex = getActiveWizard(participantWizardState)
    if (currentIndex !== -1) {
      setParticipantWizardState((val) => {
        val[currentIndex].active = false
        val[WizardState.none].active = true
        return val
      })
    }
  }

  const [formState, setFormState] = React.useState()
  const [wizardParticipants, setWizardParticipants] = React.useState([''])

  const [notificationState, setNotificationState] = React.useState(true)
  const [dialogState, showDialog, hideDialog] = useDialogState()
  const [mailingDialogState, showMailingDialog] = useDialogState()
  const { data } = useDataQuery({
    variables: { provingGroundId: proving!.provingGround!._id },
  })

  const maintenanceAreaData = React.useMemo(
    () => data && data.maintenanceAreas,
    [data]
  )
  // #endregion

  // #region Mutation hooks
  const [addParticipants] = useAddParticipantsMutation()
  const [addResourceRequirement] = useAddResourceRequirementMutation()
  const [updateParticipants] = useUpdateParticipantsMutation()
  const [deleteParticipants] = useDeleteParticipantsMutation()
  const [updateProving] = useUpdateProvingMutation()
  // #endregion

  // #region Form panel hooks
  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [editPanelState, showEditPanel, hideEditPanel] = useFormikPanelState()
  const [hotelsPanelState, showHotelsPanel, hideHotelsPanel] =
    useFormikPanelState()

  const [workerState, setWorkerState] = React.useState(false)
  const [resourceAddedState, setResourceAddedState] = React.useState(false)
  const [
    hotelsReturnStatusPanelState,
    showHotelsReturnStatusPanel,
    hideHotelsReturnStatusPanel,
  ] = useFormikPanelState()
  const [
    hotelsOutwardStatusPanelState,
    showHotelsOutwardStatusPanel,
    hideHotelsOutwardStatusPanel,
  ] = useFormikPanelState()
  const [ferryPanelState, showFerryPanel, hideFerryPanel] =
    useFormikPanelState()

  const [accomodationPanelState, showAccomodationPanel, hideAccomodationPanel] =
    useFormikPanelState()
  const [rentalCarPanelState, showRentalCarPanel, hideRentalCarPanel] =
    useFormikPanelState()

  const [timespanPanelState, showTimespanPanel, hideTimespanPanel] =
    useFormikPanelState()
  /*Uncomment for timespan panel in wizard  */
  const [
    timespanWizardPanelState,
    showTimespanWizardPanel,
    hideTimespanWizardPanel,
  ] = useFormikPanelState()
  const [groupWizardPanelState, showGroupWizardPanel, hideGroupWizardPanel] =
    useFormikPanelState()

  const [updatePanelState, showUpdatePanel, hideUpdatePanel] =
    useFormikPanelState()
  const [
    unkownParticipantPanelState,
    showUnkownParticipantPanel,
    hideUnknownParticipantPanel,
  ] = useFormikPanelState()
  const [
    participantManuellPanelState,
    showParticipantManuellPanel,
    hideParticipantManuellPanel,
  ] = useFormikPanelState()
  const [reassignPanelState, showReassignPanel, hideReassignPanel] =
    useFormikPanelState()
  const [workerPanelState, showWorkerPanel, hideWorkerPanel] =
    useFormikPanelState()
  const [supplierPanelState, showSupplierPanel, hideSupplierPanel] =
    useFormikPanelState()
  const [addGroupPanelState, showAddGroupPanel, hideAddGroupPanel] =
    useFormikPanelState()
  const [
    headOfProvingPanelState,
    showHeadOfProvingPanel,
    hideHeadOfProvingPanel,
  ] = useFormikPanelState()
  const [annotationPanelState, showAnnotationPanel, hideAnnotationPanel] =
    useFormikPanelState()
  const [updateWizardPanelState, showUpdateWizardPanel, hideUpdateWizardPanel] =
    useFormikPanelState()

  const [
    accomodationWizardPanelState,
    showAccomodationWizardPanel,
    hideAccomodationWizardPanel,
  ] = useFormikPanelState()
  // const [ferryWizardPanelState, hideFerryWizardPanel] = useFormikPanelState()
  const [
    rentalCarWizardPanelState,
    showRentalCarWizardPanel,
    hideRentalCarWizardPanel,
  ] = useFormikPanelState()
  const [hotelsWizardPanelState, showHotelsWizardPanel, hideHotelsWizardPanel] =
    useFormikPanelState()
  const [flightWizardPanelState, showFlightWizardPanel, hideFlightWizardPanel] =
    useFormikPanelState()
  const [
    participantFromProvingPanelState,
    showParticipantFromProvingPanel,
    hideParticipantFromProvingPanel,
  ] = useFormikPanelState()

  // #endregion

  const closeAllForms = () => {
    hideFlightWizardPanel()
    hideHotelsWizardPanel()
    hideRentalCarWizardPanel()
    // hideFerryWizardPanel()
    hideUpdateWizardPanel()
    hideAccomodationWizardPanel()
    hideHotelsPanel()
    hideUnknownParticipantPanel()
    hideWorkerPanel()
    /*Uncomment for timespan panel in wizard  */
    hideTimespanWizardPanel()
    hideGroupWizardPanel()
  }
  let canUpdateCurrentProving = canUpdateProving(proving)

  const isProvingResourceManager = isInProvingRole(
    proving,
    ProvingUserRole.ResourceManager
  )

  const isProvingEditor = isInProvingRole(proving, ProvingUserRole.Editor)
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const isExternal = auth.hasUserRole(UserRole.Hrg)
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  if (isServiceTeam === true) {
    canUpdateCurrentProving = isServiceTeam
  }
  const canUpdateStatus = React.useCallback(() => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (
      isAdmin ||
      isProvingResourceManager ||
      isProvingEditor ||
      isServiceTeam ||
      isExternal
    ) {
      return true
    } else {
      return false
    }
  }, [
    isExternal,
    isAdmin,
    isProvingResourceManager,
    isProvingEditor,
    isServiceTeam,
  ])

  const canUseButton = () => {
    if (canUpdateCurrentProving) {
      return true
    } else if (isExternal) {
      return true
    } else {
      return false
    }
  }

  const userCanUpdate = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (canUpdateCurrentProving) {
      return true
    } else if (
      isProvingManager &&
      (proving.status === 1 ||
        proving.status === 4 ||
        proving.status === 5 ||
        proving.status === 10)
    ) {
      return true
    } else if (isExternal) {
      return true
    } else {
      return false
    }
  }

  const userCanUseMail = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (
      isExternal ||
      isProvingEditor ||
      isProvingResourceManager ||
      isAdmin ||
      isServiceTeam
    ) {
      return true
    } else {
      return false
    }
  }

  const maintenanceHeadCanUpdate = () => {
    if (selectedItems.length !== 1) {
      return false
    } else if (
      selectedItems[0] &&
      selectedItems[0].unknownPerson &&
      selectedItems[0].unknownPerson.supplier
    ) {
      return true
    } else if (isMaintenanceHead) {
      return selectedItems[0].unknownPerson &&
        selectedItems[0].unknownPerson.personType === PersonType.Worker
        ? true
        : false
    } else {
      return true
    }
  }

  const selectedItemIsManuallyAddedSupplier = () => {
    if (selectedItems.length > 0) {
      if (
        selectedItems[0].unknownPerson &&
        selectedItems[0].unknownPerson.personType &&
        selectedItems[0].unknownPerson.personType === PersonType.Supplier &&
        selectedItems[0].unknownPerson &&
        selectedItems[0].unknownPerson.isUnknown &&
        selectedItems[0].unknownPerson.isUnknown === true
      ) {
        return true
      } else {
        return false
      }
    }
    return false
  }

  const [mailTemplateId, setMailTemplateId] = React.useState()

  // #region Submit hooks
  const navigationChangeHandler = (action: WIZARD_NAVIGATION) => {
    if (action === WIZARD_NAVIGATION.cancel) {
      resetWizardState()
      closeAllForms()
      return
    } else {
      const currentWizardIndex = getActiveWizard(participantWizardState)
      if (
        currentWizardIndex === participantWizardState.length - 1 &&
        action === WIZARD_NAVIGATION.next
      ) {
        navigationChangeHandler(WIZARD_NAVIGATION.cancel)
        return
      } // last form was submitted, do nothing
      setParticipantWizardState((val) => {
        val[currentWizardIndex].active = false
        val[currentWizardIndex + action].active = true
        return val
      })
    }
    const nextWizardState =
      participantWizardState[getActiveWizard(participantWizardState)].state
    const currentWizardParticipant = participants.find((elem) =>
      wizardParticipants.includes(elem._id)
    )

    setNotificationState(false)
    closeAllForms()

    let rentalCarWizard
    let accomodationWizard
    let flightWizard
    if (currentWizardParticipant) {
      rentalCarWizard = omitDeep(
        currentWizardParticipant.rentalCar,
        '__typename'
      )
      accomodationWizard = omitDeep(
        currentWizardParticipant.accomodation,
        '__typename'
      )
      flightWizard = omitDeep(currentWizardParticipant.flight, '__typename')
    }
    setNotificationState(false)
    switch (nextWizardState) {
      /*Uncomment for timespan panel in wizard  */
      case WizardState.timespan:
        if (currentWizardParticipant) {
          showTimespanWizardPanel({
            dateTo: currentWizardParticipant.dateTo,
            dateFrom: currentWizardParticipant.dateFrom,
          })
        } else {
          showTimespanWizardPanel({
            dateTo: proving.dateTo,
            dateFrom: proving.dateFrom,
          })
        }
        break
      case WizardState.group:
        showGroupWizardPanel()
        break
      case WizardState.accomodation:
        accomodationWizard
          ? showAccomodationWizardPanel({
              accomodation: {
                dateTo: accomodationWizard.dateTo,
                dateFrom: accomodationWizard.dateFrom,
                accomodationType: accomodationWizard.accomodationType,
                accomodation: accomodationWizard.accomodation
                  ? accomodationWizard.accomodation._id
                  : '',
                bookingStatus: accomodationWizard.bookingStatus,
              },
            })
          : showAccomodationWizardPanel()
        break
      case WizardState.hotels:
        currentWizardParticipant
          ? showHotelsWizardPanel({
              trackHotels: {
                outward: currentWizardParticipant.trackHotels!.outward!.map(
                  (item: any) => {
                    return {
                      ...omitDeep(item, ['__typename']),
                      hotel: item.hotel ? item.hotel._id : null,
                    }
                  }
                ),
                return: currentWizardParticipant.trackHotels!.return!.map(
                  (item: any) => {
                    return {
                      ...omitDeep(item, ['__typename']),
                      hotel: item.hotel ? item.hotel._id : null,
                    }
                  }
                ),
              },
            })
          : showHotelsWizardPanel({
              trackHotels: {
                outward: [],
                return: [],
              },
            })
        break
      case WizardState.orderId:
        currentWizardParticipant
          ? showUpdateWizardPanel({ orderId: currentWizardParticipant.orderId })
          : showUpdateWizardPanel()
        break
      case WizardState.rentalCar:
        rentalCarWizard
          ? showRentalCarWizardPanel({
              rentalCar: {
                ...rentalCarWizard,
                carType:
                  rentalCarWizard && rentalCarWizard.carType
                    ? rentalCarWizard.carType
                    : '',
              },
            })
          : showRentalCarWizardPanel()
        break
      case WizardState.flight:
        flightWizard
          ? showFlightWizardPanel({
              flight: {
                flightType: flightWizard.flightType,
                ...flightWizard,
              },
            })
          : showFlightWizardPanel()
        break
      default:
        break
    }
  }
  const handleAddParticipantsSubmit = React.useCallback(
    async (
      values: ProvingDetailsParticipantFormValues,
      actions: FormikActions<ProvingDetailsParticipantFormValues>
    ) => {
      actions.setSubmitting(true)
      await setResourceAddedState(false)
      const input = {
        personType: PersonType.User,
        participants: values.personIds.map((personId) => ({ personId })),
      } as ParticipantsAddInput

      const addResult = await addParticipants({
        variables: { provingId: proving._id, input },
      })

      if (addResult.data && addResult.data.addParticipants) {
        setWizardParticipants(
          addResult.data.addParticipants.map((participant) => participant._id)
        )
      }
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        hidePanel()
      } else {
        navigationChangeHandler(WIZARD_NAVIGATION.next)
      }
    },
    [
      hidePanel,
      proving,
      addParticipants,
      refetch,
      selection,
      setWizardParticipants,
      participantWizardState,
      navigationChangeHandler,
    ]
  )
  const handleUnkownParticipantSubmit = React.useCallback(
    async (
      values: ProvingDetailsParticipantsUnknownFormValues,
      actions: FormikActions<ProvingDetailsParticipantsUnknownFormValues>
    ) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((participant) => participant._id)
      await setResourceAddedState(false)
      const { personType, amount } = values

      const input: ParticipantsAddInput = {
        personType,
        participants: times(amount, (_) => ({
          unknownPerson: {
            personType,
            isUnknown: true,
          },
        })),
      }

      const addResult = await addParticipants({
        variables: {
          provingId: proving._id,
          input,
        },
      })
      if (addResult.data && addResult.data.addParticipants) {
        setWizardParticipants(
          addResult.data.addParticipants.map((participant) => participant._id)
        )
      }
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      refetch()
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        hideUnknownParticipantPanel()
      } else {
        navigationChangeHandler(WIZARD_NAVIGATION.next)
      }
    },
    [
      hideUnknownParticipantPanel,
      addParticipants,
      proving,
      refetch,
      selection,
      setWizardParticipants,
      participantWizardState,
      navigationChangeHandler,
    ]
  )

  const calculateMaintenanceAreas = React.useCallback(
    (maData, newMaData) => {
      const maintenanceHeads: User[] = [] as User[]
      const maintenanceAreas: string[] = newMaData
      if (proving && proving.participants) {
        proving.participants.forEach((p) => {
          if (p.unknownPerson && p.unknownPerson.department) {
            maintenanceAreas.push(p.unknownPerson.department)
          }
        })
      }

      if (maData) {
        maData.forEach((a: any) => {
          if (maintenanceAreas.includes(a.maintenanceArea)) {
            a.maintenanceHead.map((amh: any) => maintenanceHeads.push(amh))
          }
        })
      }
      return uniqBy(maintenanceHeads, (e) => e._id)
    },
    [data, proving, maintenanceAreaData, refetch]
  )

  const handleWorkerSubmit = React.useCallback(
    async (
      values: ProvingDetailsWorkerFormValues,
      actions: FormikActions<ProvingDetailsWorkerFormValues>
    ) => {
      actions.setSubmitting(true)
      await setWorkerState(true)
      await setResourceAddedState(false)

      const input = {
        personType: PersonType.Worker,
        participants: values.participants.map((p) => ({
          orderId: p.orderId,
          percentage: p.percentage,
          unknownPerson: {
            personType: PersonType.Worker,
            isUnknown: true,
            department: p.unknownPerson!.department,
          },
        })),
      } as ParticipantsAddInput

      const addResult = await addParticipants({
        variables: {
          provingId: proving._id,
          input,
        },
      })

      if (addResult.data && addResult.data.addParticipants) {
        setWizardParticipants(
          addResult.data.addParticipants.map(
            (participant: any) => participant._id
          )
        )
      }

      actions.setSubmitting(false)
      selection.setAllSelected(false)

      const newMaintenanceAreas: string[] = []
      values.participants.forEach((i) =>
        newMaintenanceAreas.push(i!.unknownPerson!.department!)
      )

      const calculatedMaintenanceHeads = calculateMaintenanceAreas(
        maintenanceAreaData,
        newMaintenanceAreas
      )

      if (getActiveWizard(participantWizardState) === WizardState.none) {
        hideWorkerPanel()
      } else {
        navigationChangeHandler(WIZARD_NAVIGATION.next)
      }

      await updateProving({
        variables: {
          id: proving.id,
          update: { maintenanceHead: calculatedMaintenanceHeads },
        },
      }).then((_) => refetch())
    },
    [
      calculateMaintenanceAreas,
      data,
      hideWorkerPanel,
      participantWizardState,
      proving,
      addParticipants,
      refetch,
      selection,
      setWizardParticipants,
      navigationChangeHandler,
      updateProving,
      maintenanceAreaData,
    ]
  )

  const handleHotelsSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((participant) => participant._id)

      const updateValues = removeNullProperties({
        trackHotelRequired: values.trackHotelRequired,
        trackHotels: values.trackHotels,
      })

      let newValues: any =
        updateValues &&
        updateValues.trackHotels &&
        updateValues.trackHotels.outward
          ? updateValues.trackHotels.outward.forEach((o: any) => {
              // Remove id field that we used as keys in the Form for FieldArray.
              if (o.id) {
                delete o.id
              }
              if (
                o.bookingStatus === BookingStatus.NotRequired ||
                !o.bookingStatus
              ) {
                o.bookingStatus = BookingStatus.Open
              }
            })
          : updateValues
      newValues =
        updateValues &&
        updateValues.trackHotels &&
        updateValues.trackHotels.return
          ? updateValues.trackHotels.return.forEach((r: any) => {
              if (r.id) {
                delete r.id
              }
              if (
                r.bookingStatus === BookingStatus.NotRequired ||
                !r.bookingStatus
              ) {
                r.bookingStatus = BookingStatus.Open
              }
            })
          : newValues

      let batch: ParticipantBatchInput[]
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        // No wizard
        batch = selectedItems.map((sel) => ({
          _id: sel._id,
          update: values,
        }))
      } else {
        batch = wizardParticipants.map((participant) => ({
          _id: participant,
          update: values,
        }))
      }

      addIndexToTrackHotels(batch)

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      refetch()
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        hideHotelsPanel()
      } else {
        navigationChangeHandler(WIZARD_NAVIGATION.next)
      }
    },
    [
      selectedItems,
      proving,
      updateParticipants,
      refetch,
      selection,
      hideHotelsPanel,
      participantWizardState,
      navigationChangeHandler,
      wizardParticipants,
    ]
  )

  const handleAccomodationsSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((participant) => participant._id)
      let newValues: ParticipantUpdateInput = values

      if (
        newValues.accomodation &&
        newValues.accomodation.bookingStatus &&
        newValues.accomodation.accomodationType &&
        newValues.accomodation.bookingStatus === BookingStatus.NotRequired &&
        newValues.accomodation.accomodationType ===
          AccomodationType.AccomodationWithPreference
      ) {
        newValues.accomodation.bookingStatus = BookingStatus.Open
      } else if (
        newValues.accomodation &&
        !newValues.accomodation.bookingStatus
      ) {
        newValues.accomodation.bookingStatus = BookingStatus.Open
      } else {
        newValues = values
      }

      let batch: ParticipantBatchInput[]
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        // No wizard
        batch = selectedItems.map((sel) => ({
          _id: sel._id,
          update: values,
        }))
      } else {
        batch = wizardParticipants.map((participant) => ({
          _id: participant,
          update: values,
        }))
      }

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())

      mailChangesRefetch()

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        hideAccomodationPanel()
      } else {
        navigationChangeHandler(WIZARD_NAVIGATION.next)
      }
    },
    [
      hideAccomodationPanel,
      selectedItems,
      proving,
      updateParticipants,
      refetch,
      selection,
      wizardParticipants,
      participantWizardState,
      navigationChangeHandler,
    ]
  )

  const handleRentalCarSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((participant) => participant._id)

      const updateValues = removeNullProperties({
        dateTo: values.dateTo,
        dateFrom: values.dateFrom,
        note: values.note,
        rentalCar: values.rentalCar,
      })
      let newValues: ParticipantUpdateInput = updateValues

      if (
        newValues.rentalCar!.bookingStatus === BookingStatus.NotRequired &&
        newValues.rentalCar!.carType !== CarType.None
      ) {
        newValues!.rentalCar!.bookingStatus = BookingStatus.Open
      } else if (!newValues.rentalCar!.bookingStatus) {
        newValues!.rentalCar!.bookingStatus = BookingStatus.Open
      } else {
        newValues = values
      }
      let batch: ParticipantBatchInput[]
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        // No wizard
        batch = selectedItems.map((sel) => ({
          _id: sel._id,
          update: newValues,
        }))
      } else {
        batch = wizardParticipants.map((participant) => ({
          _id: participant,
          update: newValues,
        }))
      }

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        hideRentalCarPanel()
      } else {
        navigationChangeHandler(WIZARD_NAVIGATION.next)
      }
    },
    [
      selectedItems,
      proving,
      updateParticipants,
      hideRentalCarPanel,
      participantWizardState,
      refetch,
      selection,
      wizardParticipants,
      navigationChangeHandler,
    ]
  )

  const handleFerrySubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((participant) => participant._id)
      const updateValues = removeNullProperties({
        note: values.note,
        ferry: values.ferry,
      })
      let newValues: ParticipantUpdateInput = updateValues

      if (
        (newValues.ferry!.bookingStatus === BookingStatus.NotRequired &&
          newValues.ferry!.outward) ||
        (newValues.ferry!.bookingStatus === BookingStatus.NotRequired &&
          newValues.ferry!.return)
      ) {
        newValues!.ferry!.bookingStatus = BookingStatus.Open
      } else {
        newValues = updateValues
      }
      let batch: ParticipantBatchInput[]
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        // No wizard
        batch = selectedItems.map((sel) => ({
          _id: sel._id,
          update: updateValues,
        }))
      } else {
        batch = wizardParticipants.map((participant) => ({
          _id: participant,
          update: updateValues,
        }))
      }
      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      hideFerryPanel()
      const currentWizardIndex = getActiveWizard(participantWizardState)
      switch (participantWizardState[currentWizardIndex].state) {
        case WizardState.rentalCar:
          showRentalCarWizardPanel()
          break
        case WizardState.hotels:
          showHotelsWizardPanel({
            trackHotels: {
              outward: [],
              return: [],
            },
          })
          break
        case WizardState.flight:
          showFlightWizardPanel()
          break
        case WizardState.nordschleife:
          showUpdateWizardPanel()
          break
        case WizardState.ferry:
          // hideFerryWizardPanel()
          resetWizardState()
          break
        default:
          break
      }
    },
    [
      selectedItems,
      proving,
      updateParticipants,
      hideFerryPanel,
      refetch,
      selection,
      showUpdateWizardPanel,
      showFlightWizardPanel,
      showHotelsWizardPanel,
      // hideFerryWizardPanel,
      showRentalCarWizardPanel,
      participantWizardState,
      resetWizardState,
      wizardParticipants,
    ]
  )

  const handleAnnotationSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)
      const { errors } = await updateProving({
        variables: {
          id: proving.id,
          update: {
            annotationParticipants: values.annotation,
          },
        },
      })
      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      refetch()
      hideAnnotationPanel()
    },
    [proving, updateProving, hideAnnotationPanel, refetch]
  )

  const handleTimespanSubmit = React.useCallback(
    (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((participant) => participant._id)

      const updatedParticipants = selectedItems.map((s) => {
        return {
          _id: s._id,
          update: {
            dateFrom: values.dateFrom,
            dateTo: values.dateTo,
            accomodation: {
              dateFrom:
                s.accomodation && s.accomodation.dateFrom
                  ? values.dateFrom
                  : undefined,
              dateTo:
                s.accomodation && s.accomodation.dateTo
                  ? values.dateTo
                  : undefined,
            },
            flight: {
              outboundData: {
                departure:
                  s.flight &&
                  s.flight.outboundData &&
                  s.flight.outboundData.departure
                    ? values.dateFrom
                    : undefined,
              },
              returnData: {
                departure:
                  s.flight &&
                  s.flight.returnData &&
                  s.flight.returnData.departure
                    ? values.dateTo
                    : undefined,
              },
            },
            rentalCar: {
              dateFrom:
                s.rentalCar && s.rentalCar.dateFrom
                  ? values.dateFrom
                  : undefined,
              dateTo:
                s.rentalCar && s.rentalCar.dateTo ? values.dateTo : undefined,
            },
          },
        }
      })

      updateParticipants({
        variables: {
          provingId: proving._id,
          batch: updatedParticipants,
        },
      }).then((_) => refetch())

      hideTimespanPanel()
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    },
    [updateParticipants, refetch, selection, hideTimespanPanel, selectedItems]
  )

  const handleUpdateSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((participant) => participant._id)
      const updatedValues = removeNullProperties({
        trackHotels: values.trackHotels,
        note: values.note,
        ...values,
      })
      let batch: ParticipantBatchInput[]
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        // No wizard
        batch = selectedItems.map((sel) => ({
          _id: sel._id,
          update: updatedValues,
        }))
      } else {
        batch = wizardParticipants.map((participant) => ({
          _id: participant,
          update: updatedValues,
        }))
      }

      addIndexToTrackHotels(batch)

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())

      if (
        getActiveWizardState(participantWizardState) === WizardState.timespan &&
        wizardParticipants.length !== 0
      ) {
        await addResourceRequirement({
          variables: {
            provingId: proving._id,
            input: {
              resourceRequirementType: ResourceSelectionType.Workstation,
              amount: wizardParticipants.length,
              dateFrom: values.dateFrom,
              dateTo: values.dateTo,
              orderIdDistribution: [],
            },
          },
        }).then((_) => refetch())
        await setResourceAddedState(true)
      }
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      if (getActiveWizard(participantWizardState) === WizardState.none) {
        hideUpdatePanel()
        hideTimespanPanel()
        hideRentalCarPanel()
        hideAccomodationPanel()
        hideHotelsPanel()
        hideFerryPanel()
        hideHotelsOutwardStatusPanel()
        hideHotelsReturnStatusPanel()
        hideAnnotationPanel()
      } else {
        navigationChangeHandler(WIZARD_NAVIGATION.next)
      }
    },
    [
      addResourceRequirement,
      selectedItems,
      proving,
      updateParticipants,
      hideUpdatePanel,
      refetch,
      selection,
      hideTimespanPanel,
      hideRentalCarPanel,
      hideAccomodationPanel,
      hideHotelsPanel,
      hideFerryPanel,
      hideHotelsOutwardStatusPanel,
      hideHotelsReturnStatusPanel,
      hideAnnotationPanel,
      participantWizardState,
      navigationChangeHandler,
      wizardParticipants,
      workerState,
    ]
  )

  const handleTimespanWizardDismiss = React.useCallback(async () => {
    if (!workerState && !resourceAddedState) {
      await addResourceRequirement({
        variables: {
          provingId: proving._id,
          input: {
            resourceRequirementType: ResourceSelectionType.Workstation,
            amount: wizardParticipants.length,
            dateFrom: proving.dateFrom,
            dateTo: proving.dateTo,
            orderIdDistribution: [],
          },
        },
      })
    }
    refetch()
    await setResourceAddedState(true)
    await setWorkerState(false)
  }, [
    addResourceRequirement,
    wizardParticipants,
    proving,
    refetch,
    resourceAddedState,
    workerState,
  ])

  const handleWizardFlightSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      ;(values as any).flight.bookingStatus = BookingStatus.NotRequired
      if (
        values.flight &&
        values.flight.bookingStatus &&
        values.flight.flightType !== FlightType.NoFlight
      ) {
        values.flight.bookingStatus = BookingStatus.Open
      }
      values = removeNullProperties({ ...values })
      actions.setSubmitting(true)
      let batch: ParticipantBatchInput[]
      batch = wizardParticipants.map((participant) => ({
        _id: participant,
        update: values,
      }))
      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      navigationChangeHandler(WIZARD_NAVIGATION.next)
    },
    [
      proving,
      updateParticipants,
      refetch,
      wizardParticipants,
      navigationChangeHandler,
    ]
  )

  const handleManuellSubmit = React.useCallback(
    async (
      values: any,
      actions: FormikActions<ProvingDetailsManuellParticipantFormValues>
    ) => {
      actions.setSubmitting(true)
      const input: ParticipantsAddInput = {
        personType: values.personType,
        participants: [
          {
            unknownPerson: {
              personType: values.personType,
              isUnknown: false,
              firstName:
                values.participants.unknownPerson &&
                values.participants.unknownPerson.firstName
                  ? values.participants.unknownPerson.firstName
                  : '',
              surName:
                values.participants.unknownPerson &&
                values.participants.unknownPerson.surName
                  ? values.participants.unknownPerson.surName
                  : '',
              email:
                values.participants.unknownPerson &&
                values.participants.unknownPerson.email
                  ? values.participants.unknownPerson.email
                  : '',
              supplier:
                values.participants.unknownPerson &&
                values.participants.unknownPerson.supplier
                  ? values.participants.unknownPerson.supplier
                  : '',
            },
          },
        ],

        // values.participants.map(p => ({
        //   unknownPerson: {
        //     personType: values.personType,
        //     isUnknown: true,
        //     firstName:
        //       p.unknownPerson && p.unknownPerson.firstName
        //         ? p.unknownPerson!.firstName
        //         : '',
        //     surName:
        //       p.unknownPerson && p.unknownPerson.surName
        //         ? p.unknownPerson.surName
        //         : '',
        //     email:
        //       p.unknownPerson && p.unknownPerson.email
        //         ? p.unknownPerson.email
        //         : '',
        //   },
        // })),
      }
      const { errors } = await addParticipants({
        variables: {
          provingId: proving._id,
          input,
        },
      })

      if (errors) {
        throw errors
      }

      actions.setSubmitting(false)
      refetch()
      hideParticipantManuellPanel()
    },
    [hideParticipantManuellPanel, proving, addParticipants, refetch]
  )

  const handleSupplierSubmit = React.useCallback(
    async (
      values: ProvingDetailsSupplierFormValues,
      actions: FormikActions<ProvingDetailsSupplierFormValues>
    ) => {
      actions.setSubmitting(true)

      const input: ParticipantsAddInput = {
        personType: PersonType.Supplier,
        participants: values.participants.map((p) => ({
          unknownPerson: {
            personType: PersonType.Supplier,
            isUnknown: true,
            supplier: p.unknownPerson!.supplier,
            employee: p.unknownPerson!.employee,
            firstName: p.unknownPerson!.firstName,
            surName: p.unknownPerson!.surName,
            email: p.unknownPerson!.email,
          },
        })),
      }

      const { errors } = await addParticipants({
        variables: {
          provingId: proving._id,
          input,
        },
      })

      if (errors) {
        throw errors
      }

      actions.setSubmitting(false)
      selection.setAllSelected(false)
      refetch()
      hideSupplierPanel()
    },
    [hideSupplierPanel, proving, addParticipants, refetch, selection]
  )
  const handleConfirmDelete = React.useCallback(async () => {
    const idsToDelete = selectedItems.map((sel) => sel._id)
    await deleteParticipants({
      variables: {
        provingId: proving._id,
        idsToDelete,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    hideDialog()
  }, [
    selectedItems,
    hideDialog,
    deleteParticipants,
    proving,
    refetch,
    selection,
  ])

  const handleReassignSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)
      const selectedKeys = selectedItems.map((participant) => participant._id)

      const batch: ParticipantBatchInput[] = [
        {
          _id: selectedItems[0]._id,
          update: values,
        },
      ]

      await updateParticipants({
        variables: { provingId: proving._id, batch },
      }).then((_) => refetch())
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))

      hideReassignPanel()
    },
    [
      selectedItems,
      updateParticipants,
      hideReassignPanel,
      proving,
      refetch,
      selection,
    ]
  )

  const handleGroupAssign = React.useCallback(
    async (
      values: { group: string },
      actions: FormikActions<{ group: string }>
    ) => {
      actions.setSubmitting(true)

      const batch: ParticipantBatchInput[] = selectedItems.map((p) => ({
        _id: p._id,
        update: {
          group: values.group,
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())

      const selectedKeys = selectedItems.map((participant) => participant._id)
      actions.setSubmitting(false)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      hideAddGroupPanel()
    },
    [
      selection,
      selectedItems,
      refetch,
      hideAddGroupPanel,
      proving,
      updateParticipants,
    ]
  )
  const handleRemoveGroup = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((p) => ({
      _id: p._id,
      update: {
        group: '',
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleParticiantsFromProvingSubmit = React.useCallback(
    async (
      values: { participants: ParticipantInsertInput[] },
      actions: FormikActions<any>
    ) => {
      actions.setSubmitting(true)
      await addParticipants({
        variables: {
          provingId: proving._id,
          input: {
            personType: PersonType.Worker,
            participants: values.participants,
          },
        },
      })
      const workstationCount = values.participants.filter(
        (p) =>
          !!p.personId &&
          !(p.unknownPerson && p.unknownPerson.personType === PersonType.Worker)
      ).length
      if (workstationCount > 0) {
        await addResourceRequirement({
          variables: {
            provingId: proving._id,
            input: {
              resourceRequirementType: ResourceSelectionType.Workstation,
              amount: workstationCount,
              dateFrom: proving.dateFrom,
              dateTo: proving.dateTo,
              orderIdDistribution: [],
            },
          },
        })
      }
      await setResourceAddedState(true)
      actions.setSubmitting(false)
      hideParticipantFromProvingPanel()
      await refetch()
    },
    [
      addParticipants,
      addResourceRequirement,
      hideParticipantFromProvingPanel,
      proving._id,
      proving.dateFrom,
      proving.dateTo,
      refetch,
    ]
  )

  // #endregion

  // #region Command hooks
  useCommand(
    {
      key: 'addParticipant',
      priority: 10,
      text: t('model.proving.participant.addParticipant'),
      iconProps: { iconName: 'Contact' },
      disabled: !canUpdateCurrentProving && !isMaintenanceHead,
      hidden: isExternal!,
      subMenuProps: {
        items: [
          {
            key: 'knownParticipant',
            disabled: !!isMaintenanceHead,
            text: t('model.proving.participant.participantIsKnown'),
            onClick: () => {
              showPanel({
                personType: '',
                participants: [],
              })
              setParticipantWizardState(initWizardStates(false))
              setFormState({ formType: FormType.addParticipant })
              setWizardParticipants([])
              const currentIndex = getActiveWizard(participantWizardState)
              setParticipantWizardState((val) => {
                val[currentIndex].active = false
                val[WizardState.addParticipant].active = true
                return val
              })
            },
          },
          {
            key: 'unknownParticipant',
            text: t('model.proving.participant.participantIsUnknown'),
            disabled: !!isMaintenanceHead,
            onClick: () => {
              setParticipantWizardState(initWizardStates(false))
              setFormState({ formType: FormType.addParticipant })
              setWizardParticipants([''])
              const currentIndex = getActiveWizard(participantWizardState)
              setParticipantWizardState((val) => {
                val[currentIndex].active = false
                val[WizardState.addParticipant].active = true
                return val
              })
              showUnkownParticipantPanel({ amount: 1 })
            },
          },
          {
            key: 'addWorker',
            text: t('model.proving.participant.participantWorker'),
            disabled: !!isMaintenanceHead,
            onClick: () => {
              setParticipantWizardState(initWizardStates(true))
              setFormState({ formType: FormType.addParticipant })
              setWizardParticipants([])
              const currentIndex = getActiveWizard(participantWizardState)
              setParticipantWizardState((val) => {
                val[currentIndex].active = false
                val[WizardState.addParticipant].active = true
                return val
              })
              showWorkerPanel({
                personType: 'worker',
                participants: [],
              })
            },
          },
          {
            key: 'addSupplier',
            text: t('model.proving.participant.participantSupplier'),
            disabled: !!isMaintenanceHead,
            onClick: () => {
              showSupplierPanel({
                personType: 'supplier',
                participants: [],
              })
            },
          },
          {
            key: 'addParticipantManuelly',
            text: t('model.proving.participant.participantManuel'),
            disabled: !isAdmin && !isProvingResourceManager,
            onClick: () => {
              showParticipantManuellPanel({
                participants: [],
              })
            },
          },
          {
            key: 'reassignParticipant',
            text: t('model.proving.participant.assignParticipant'),
            disabled:
              !maintenanceHeadCanUpdate() ||
              selectedItemIsManuallyAddedSupplier(),
            onClick: () => {
              showReassignPanel({
                personType: selectedItems[0].person
                  ? selectedItems[0].person.type
                  : selectedItems[0].unknownPerson!.personType,
              })
            },
          },
          {
            key: 'participantFromProving',
            text: t('model.proving.participant.participantFromProving'),
            disabled: !!isMaintenanceHead,
            onClick: () => {
              showParticipantFromProvingPanel()
            },
          },
        ],
      },
    },
    [selectedItems, isProvingEditor, setWizardParticipants]
  )

  //#region TIMESPAN
  const setTimespanDateToProvingDate = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        dateFrom: proving.dateFrom,
        dateTo: proving.dateTo,
        accomodation: {
          dateFrom:
            sel.accomodation && sel.accomodation.dateFrom
              ? proving.dateFrom
              : undefined,
          dateTo:
            sel.accomodation && sel.accomodation.dateTo
              ? proving.dateTo
              : undefined,
        },
        flight: {
          outboundData: {
            departure:
              sel.flight &&
              sel.flight.outboundData &&
              sel.flight.outboundData.departure
                ? proving.dateFrom
                : undefined,
          },
          returnData: {
            departure:
              sel.flight &&
              sel.flight.returnData &&
              sel.flight.returnData.departure
                ? proving.dateTo
                : undefined,
          },
        },
        rentalCar: {
          dateFrom:
            sel.rentalCar && sel.rentalCar.dateFrom
              ? proving.dateFrom
              : undefined,
          dateTo:
            sel.rentalCar && sel.rentalCar.dateTo ? proving.dateTo : undefined,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  useCommand(
    {
      key: 'timespan',
      priority: 20,
      disabled:
        selectedItems.length === 0 ||
        !canUpdateCurrentProving ||
        !!isMaintenanceHead,
      text: t('field.timespan.label'),
      iconProps: { iconName: 'CalendarWeek' },
      hidden: isExternal!,
      subMenuProps: {
        items: [
          {
            key: 'setTimespan',
            text: t('field.timespan.set'),
            onClick: () => {
              if (selectedItems.length > 1) {
                setNotificationState(true)
                showTimespanPanel()
              } else {
                setNotificationState(false)
                showTimespanPanel({
                  dateTo: selectedItems[0].dateTo,
                  dateFrom: selectedItems[0].dateFrom,
                })
              }
              setFormState({ formType: FormType.timespan })
            },
          },
          {
            key: 'setDateToProvingDate',
            text: t('field.timespan.reset'),
            onClick: () => {
              setTimespanDateToProvingDate()
            },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  //#endregion TIMESPAN
  //#region ORDERID
  const setNoOrderId = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        orderId: '',
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  useCommand(
    {
      key: 'orderId',
      priority: 30,
      disabled:
        selectedItems.length === 0 ||
        !canUpdateCurrentProving ||
        !!isMaintenanceHead,
      text: t('model.proving.participant.orderId'),
      iconProps: { iconName: 'DonutChart' },
      hidden: isExternal!,
      subMenuProps: {
        items: [
          {
            key: 'assignOrderId',
            text: t('field.orderId.assignOrderId'),
            onClick: () => {
              if (selectedItems.length > 1) {
                setNotificationState(true)
                showUpdatePanel()
              } else {
                setNotificationState(false)
                showUpdatePanel({ orderId: selectedItems[0].orderId })
              }
              setFormState({ formType: FormType.orderId })
            },
          },
          {
            key: 'noOrderId',
            text: t('field.orderId.noOrderId'),
            onClick: () => {
              setNoOrderId()
            },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  //#endregion ORDERID
  //#region ACCOMODATION
  const handleAccomodationBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          accomodation: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleAccomodationBookingStatusOpenSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          accomodation: {
            bookingStatus: BookingStatus.Open,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleAccomodationBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          accomodation: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleAccomodationBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          accomodation: {
            bookingStatus: BookingStatus.Finished,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleNoAccomodation = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        accomodation: {
          accomodation: null,
          accomodationType: AccomodationType.NoAccomodation,
          dateFrom: null,
          dateTo: null,
          bookingStatus: BookingStatus.NotRequired,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  useCommand(
    {
      key: 'accomodation',
      priority: 40,
      disabled:
        selectedItems.length === 0 || !userCanUpdate() || !!isMaintenanceHead,
      text: t('model.proving.participant.accomodation'),
      iconProps: { iconName: 'Hotel' },
      subMenuProps: {
        items: [
          {
            key: 'setAccomodation',
            text: t('field.accomodation.setAccomodation.label'),
            onClick: () => {
              const accomodation = omitDeep(
                selectedItems[0].accomodation,
                '__typename'
              )
              if (selectedItems.length > 1) {
                setNotificationState(true)
                showAccomodationPanel()
              } else {
                setNotificationState(false)
                showAccomodationPanel({
                  accomodation: {
                    dateTo: accomodation.dateTo
                      ? accomodation.dateTo
                      : selectedItems[0].dateTo,
                    dateFrom: accomodation.dateFrom
                      ? accomodation.dateFrom
                      : selectedItems[0].dateFrom,
                    accomodationType:
                      accomodation.accomodationType ===
                      AccomodationType.NoAccomodation
                        ? AccomodationType.AccomodationWithPreference
                        : accomodation.accomodationType,
                    accomodation: accomodation.accomodation
                      ? accomodation.accomodation._id
                      : '',
                    bookingStatus: accomodation.bookingStatus,
                  },
                })
              }
              setFormState({ formType: FormType.accomodation })
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'noAccomodation',
            text: t('field.accomodation.noAccomodation'),
            onClick: () => {
              handleNoAccomodation()
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'setAccomodationStatus',
            text: t('field.status.changeStatus.label'),
            disabled: !canUpdateStatus(),
            subMenuProps: {
              items: [
                {
                  key: 'accomodationBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleAccomodationBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'accomodationBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleAccomodationBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'accomodationBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleAccomodationBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'accomodationBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleAccomodationBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
            // onClick: () => {
            //   const accomodation = omitDeep(
            //     selectedItems[0].accomodation,
            //     '__typename'
            //   )
            //   if (selectedItems.length > 1) {
            //     showAccomodationStatusPanel()
            //   } else {
            //     showAccomodationStatusPanel({
            //       accomodation: {
            //         bookingStatus: accomodation.bookingStatus,
            //       },
            //     })
            //   }
            //   setFormState({ formType: FormType.accomodationStatus })
            // },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  //#endregion ACCOMODATION
  //#region RENTALCAR
  const handleRentalCarBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          rentalCar: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleRentalCarBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        rentalCar: {
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleRentalCarBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          rentalCar: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleRentalCarBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          rentalCar: {
            bookingStatus: BookingStatus.Finished,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleRentalCarBookingStatusFleetVehicleSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          rentalCar: {
            bookingStatus: BookingStatus.FleetVehicle,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleNoRentalCar = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        rentalCar: {
          carType: CarType.None,
          bookingStatus: BookingStatus.NotRequired,
          dateFrom: null,
          dateTo: null,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  useCommand(
    {
      key: 'rentalCar',
      priority: 50,
      disabled:
        selectedItems.length === 0 || !canUseButton() || !!isMaintenanceHead,
      text: t('model.proving.participant.rentalCar'),
      iconProps: { iconName: 'Car' },
      subMenuProps: {
        items: [
          {
            key: 'setRentalCar',
            text: t('field.rentalCar.setRentalCar.label'),
            onClick: () => {
              const rentalCar = omitDeep(
                selectedItems[0].rentalCar,
                '__typename'
              )
              if (selectedItems.length > 1) {
                setNotificationState(true)
                showRentalCarPanel()
              } else {
                setNotificationState(false)
                showRentalCarPanel({
                  rentalCar: {
                    ...rentalCar,
                    carType:
                      rentalCar && rentalCar.carType ? rentalCar.carType : '',
                  },
                })
              }
              setFormState({ formType: FormType.rentalCar })
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'noRentalCar',
            text: t('field.rentalCar.noRentalCar'),
            onClick: () => {
              handleNoRentalCar()
            },
            hidden: isExternal,
            disabled: isExternal,
          },
          {
            key: 'setRentalCarStatus',
            text: t('field.status.changeStatus.label'),
            disabled: !canUpdateStatus(),
            subMenuProps: {
              items: [
                {
                  key: 'rentalCarBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleRentalCarBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'rentalCarBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleRentalCarBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'rentalCarBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleRentalCarBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'rentalCarBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleRentalCarBookingStatusFinishedSubmit()
                  },
                },
                {
                  key: 'rentalCarBookingStatusFinished',
                  text: t('enum.bookingStatus.fleetVehicle'),
                  disabled: proving.provingGround!.title !== 'Arjeplog',
                  onClick: () => {
                    handleRentalCarBookingStatusFleetVehicleSubmit()
                  },
                },
              ],
            },
            // onClick: () => {
            //   const rentalCar = omitDeep(
            //     selectedItems[0].rentalCar,
            //     '__typename'
            //   )
            //   if (selectedItems.length > 1) {
            //     showRentalCarStatusPanel()
            //   } else {
            //     showRentalCarStatusPanel({
            //       rentalCar: {
            //         bookingStatus: rentalCar.bookingStatus,
            //       },
            //     })
            //   }
            //   setFormState({ formType: FormType.rentalCarStatus })
            // },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  //#endregion RENTALCAR

  // const handleRemoveGroup = React.useCallback(async () => {
  //   const batch: ParticipantBatchInput[] = selectedItems.map(p => ({
  //     _id: p._id,
  //     update: {
  //       group: '',
  //     },
  //   }))

  //   await updateParticipants({
  //     variables: {
  //       provingId: proving._id,
  //       batch,
  //     },
  //   })
  //   selection.setAllSelected(false)
  //   refetch()
  // }, [proving, selectedItems, updateParticipants, refetch, selection])

  //#region FLIGHT
  const handleNoFlightSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          flightType: FlightType.NoFlight,
          bookingStatus: BookingStatus.NotRequired,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])
  const handleReturnFlightSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          flightType: FlightType.ReturnFlight,
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleOutboundFlightSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          flightType: FlightType.OutboundFlight,
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])
  const handleRoundtripFlightSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          flightType: FlightType.Roundtrip,
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleFlightBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          flight: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleFlightBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        flight: {
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleFlightBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          flight: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleFlightBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          flight: {
            bookingStatus: BookingStatus.Finished,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  useCommand(
    {
      key: 'flight',
      priority: 60,
      disabled:
        selectedItems.length === 0 || !canUseButton() || !!isMaintenanceHead,
      hidden: !!proving.provingGround!.hideFlight,
      text: t('model.proving.participant.flight'),
      iconProps: { iconName: 'Airplane' },
      subMenuProps: {
        items: [
          {
            key: 'roundTripFlight',
            text: t('enum.flight.flightType.roundtrip'),
            onClick: () => {
              handleRoundtripFlightSubmit()
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'outboundFlight',
            text: t('enum.flight.flightType.outboundFlight'),
            onClick: () => {
              handleOutboundFlightSubmit()
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'returnFlight',
            text: t('enum.flight.flightType.returnFlight'),
            onClick: () => {
              handleReturnFlightSubmit()
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'noFlight',
            text: t('enum.flight.flightType.noFlightText'),
            onClick: () => {
              handleNoFlightSubmit()
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          // {
          //   key: 'setFlight',
          //   text: t('field.flight.setFlight.label'),
          //   hidden: true,
          //   onClick: () => {
          //     const flight = omitDeep(selectedItems[0].flight, '__typename')
          //     if (selectedItems.length > 1) {
          //       setNotificationState(true)
          //       showFlightPanel()
          //     } else {
          //       setNotificationState(false)
          //       showFlightPanel({
          //         flight: {
          //           flightType: flight.flightType,
          //           bookingStatus: flight.bookingStatus,
          //         },
          //       })
          //     }
          //     setFormState({ formType: FormType.flight })
          //   },
          // },
          {
            key: 'setFlightStatus',
            text: t('field.status.changeStatus.label'),
            disabled: !canUpdateStatus(),
            subMenuProps: {
              items: [
                {
                  key: 'flightBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleFlightBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'flightBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleFlightBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'flightBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleFlightBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'flightBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleFlightBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
            // onClick: () => {
            //   const flight = omitDeep(selectedItems[0].flight, '__typename')
            //   if (selectedItems.length > 1) {
            //     showFlightStatusPanel()
            //   } else {
            //     setNotificationState(false)
            //     showFlightStatusPanel({
            //       flight: {
            //         bookingStatus: flight.bookingStatus,
            //       },
            //     })
            //   }
            //   setFormState({ formType: FormType.flightStatus })
            // },
          },
        ],
      },
    },
    [showPanel, setFormState, selectedItems, canUpdateCurrentProving, refetch]
  )
  //#endregion FLIGHT
  //#region FERRY
  const handleFerryBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          ferry: {
            bookingStatus: BookingStatus.NotRequired,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleFerryBookingStatusOpenSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        ferry: {
          bookingStatus: BookingStatus.Open,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleFerryBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          ferry: {
            bookingStatus: BookingStatus.WorkInProgress,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleFerryBookingStatusFinishedSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        ferry: {
          bookingStatus: BookingStatus.Finished,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleNoFerry = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        ferry: {
          outward: null,
          return: null,
          bookingStatus: BookingStatus.NotRequired,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  useCommand(
    {
      key: 'ferry',
      priority: 70,
      text: t('field.ferry.label'),
      disabled:
        selectedItems.length === 0 || !canUseButton() || !!isMaintenanceHead,
      hidden: !!proving.provingGround!.hideFerryConnection,
      iconProps: { iconName: 'Ferry' },
      subMenuProps: {
        items: [
          {
            key: 'setFerry',
            text: t('field.ferry.setFerry.label'),
            onClick: () => {
              const ferry = omitDeep(selectedItems[0].ferry, '__typename')
              selectedItems.length > 1
                ? setNotificationState(true)
                : setNotificationState(false)

              showFerryPanel({
                ferry: {
                  ...ferry,
                  outward: ferry && ferry.outward ? ferry.outward._id : null,
                  return: ferry && ferry.return ? ferry.return._id : null,
                },
              })
              setFormState({ formType: FormType.ferry })
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'noFerry',
            text: t('field.ferry.noFerry'),
            onClick: () => {
              handleNoFerry()
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'setFerryStatus',
            text: t('field.status.changeStatus.label'),
            disabled: !canUpdateStatus(),
            subMenuProps: {
              items: [
                {
                  key: 'ferryBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleFerryBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'ferryBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleFerryBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'ferryBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleFerryBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'ferryBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleFerryBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
            // onClick: () => {
            //   const ferry = omitDeep(selectedItems[0].ferry, '__typename')
            //   showFerryStatusPanel({
            //     ferry: {
            //       bookingStatus:
            //         ferry && ferry.bookingStatus
            //           ? ferry.bookingStatus
            //           : BookingStatus.NotRequired,
            //     },
            //   })
            //   setFormState({ formType: FormType.ferryStatus })
            // },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  //#endregion FERRY

  //#region NORDSCHLEIFE
  const handleNordschleifeBookingStatusNotRequiredSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          nordschleifeBookingStatus: BookingStatus.NotRequired,
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])
  const handleNordschleifeBookingStatusOpenSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          nordschleifeBookingStatus: BookingStatus.Open,
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])
  const handleNordschleifeBookingStatusWorkInProgressSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          nordschleifeBookingStatus: BookingStatus.WorkInProgress,
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])
  const handleNordschleifeBookingStatusFinishedSubmit =
    React.useCallback(async () => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          nordschleifeBookingStatus: BookingStatus.Finished,
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    }, [proving, selectedItems, updateParticipants, refetch, selection])
  const handleNoNordschleifeSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        nordschleife: false,
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])
  const handleYesNordschleifeSubmit = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => {
      if (sel.nordschleifeBookingStatus) {
        return {
          _id: sel._id,
          update: {
            nordschleife: true,
          },
        }
      } else {
        return {
          _id: sel._id,
          update: {
            nordschleife: true,
            nordschleifeBookingStatus: BookingStatus.Open,
          },
        }
      }
    })

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  useCommand(
    {
      key: 'nordschleife',
      priority: 80,
      text: t('field.nordschleife.label'),
      disabled: selectedItems.length === 0 || !canUpdateCurrentProving,
      hidden:
        !!proving.provingGround!.hideNordschleife ||
        isExternal! ||
        !!isMaintenanceHead,
      iconProps: { iconName: 'Ellipse' },
      onClick: () => {
        selectedItems.length > 1
          ? setNotificationState(true)
          : setNotificationState(false)
        showUpdatePanel({
          nordschleife: selectedItems[0].nordschleife,
        })
        setFormState({ formType: FormType.nordschleife })
      },
      subMenuProps: {
        items: [
          {
            key: 'yesNordschleife',
            text: t('field.nordschleife.yesNordschleife'),
            onClick: () => {
              handleYesNordschleifeSubmit()
            },
          },
          {
            key: 'noNordschleife',
            text: t('field.nordschleife.noNordschleife'),
            onClick: () => {
              handleNoNordschleifeSubmit()
            },
          },

          {
            key: 'setNordschleifeStatus',
            text: t('field.status.changeStatus.label'),
            disabled: !canUpdateStatus(),
            subMenuProps: {
              items: [
                {
                  key: 'nordschleifeBookingStatusNotRequired',
                  text: t('enum.bookingStatus.notRequired'),
                  onClick: () => {
                    handleNordschleifeBookingStatusNotRequiredSubmit()
                  },
                },
                {
                  key: 'nordschleifeBookingStatusOpen',
                  text: t('enum.bookingStatus.open'),
                  onClick: () => {
                    handleNordschleifeBookingStatusOpenSubmit()
                  },
                },
                {
                  key: 'nordschleifeBookingStatusInProgress',
                  text: t('enum.bookingStatus.workInProgress'),
                  onClick: () => {
                    handleNordschleifeBookingStatusWorkInProgressSubmit()
                  },
                },
                {
                  key: 'nordschleifeBookingStatusFinished',
                  text: t('enum.bookingStatus.finished'),
                  onClick: () => {
                    handleNordschleifeBookingStatusFinishedSubmit()
                  },
                },
              ],
            },
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )
  //#endregion NORDSCHLEIFE

  //#region TRACKHOTEL
  const handleNoTrackHotels = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        trackHotels: {
          outward: [],
          return: [],
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleTrackHotelOutwardStatus = React.useCallback(
    async (status: BookingStatus, index: number) => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          trackHotels: {
            outward:
              sel.trackHotels &&
              sel.trackHotels.outward &&
              sel.trackHotels.outward.map((outwards, i) => {
                if (i === index) {
                  return {
                    _id: outwards._id,
                    bookingStatus: status,
                    hotel: outwards.hotel && outwards.hotel._id,
                    dateFrom: outwards.dateFrom,
                    dateTo: outwards.dateTo,
                    note: outwards.note,
                  }
                } else {
                  return {
                    _id: outwards._id,
                    bookingStatus: outwards.bookingStatus,
                    hotel: outwards.hotel && outwards.hotel._id,
                    dateFrom: outwards.dateFrom,
                    dateTo: outwards.dateTo,
                    note: outwards.note,
                  }
                }
              }),
          },
        },
      }))

      addIndexToTrackHotels(batch)

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    },
    [proving, selectedItems, updateParticipants, refetch, selection]
  )

  const handleTrackHotelReturnStatus = React.useCallback(
    async (status: BookingStatus, index: number) => {
      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          trackHotels: {
            return:
              sel.trackHotels &&
              sel.trackHotels.return &&
              sel.trackHotels.return.map((returns, i) => {
                if (i === index) {
                  return {
                    _id: returns._id,
                    bookingStatus: status,
                    hotel: returns.hotel && returns.hotel._id,
                    dateFrom: returns.dateFrom,
                    dateTo: returns.dateTo,
                    note: returns.note,
                  }
                } else {
                  return {
                    _id: returns._id,
                    bookingStatus: returns.bookingStatus,
                    hotel: returns.hotel && returns.hotel._id,
                    dateFrom: returns.dateFrom,
                    dateTo: returns.dateTo,
                    note: returns.note,
                  }
                }
              }),
          },
        },
      }))

      addIndexToTrackHotels(batch)

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    },
    [proving, selectedItems, updateParticipants, refetch, selection]
  )

  const statusItemsOutward = (index: number) => [
    {
      key: 'trackHotelOutwardStatusNotRequired',
      text: t('enum.bookingStatus.notRequired'),
      onClick: () =>
        handleTrackHotelOutwardStatus(BookingStatus.NotRequired, index),
    },
    {
      key: 'trackHotelOutwardStatusOpen',
      text: t('enum.bookingStatus.open'),
      onClick: () => {
        handleTrackHotelOutwardStatus(BookingStatus.Open, index)
      },
    },
    {
      key: 'trackHotelOutwardStatusWorkInProgress',
      text: t('enum.bookingStatus.workInProgress'),
      onClick: () => {
        handleTrackHotelOutwardStatus(BookingStatus.WorkInProgress, index)
      },
    },
    {
      key: 'trackHotelOutwardStatusFinished',
      text: t('enum.bookingStatus.finished'),
      onClick: () => {
        handleTrackHotelOutwardStatus(BookingStatus.Finished, index)
      },
    },
  ]

  const statusItemsReturn = (index: number) => [
    {
      key: 'trackHotelReturnStatusNotRequired',
      text: t('enum.bookingStatus.notRequired'),
      onClick: () =>
        handleTrackHotelReturnStatus(BookingStatus.NotRequired, index),
    },
    {
      key: 'trackHotelReturnStatusOpen',
      text: t('enum.bookingStatus.open'),
      onClick: () => {
        handleTrackHotelReturnStatus(BookingStatus.Open, index)
      },
    },
    {
      key: 'trackHotelReturnStatusWorkInProgress',
      text: t('enum.bookingStatus.workInProgress'),
      onClick: () => {
        handleTrackHotelReturnStatus(BookingStatus.WorkInProgress, index)
      },
    },
    {
      key: 'trackHotelReturnStatusFinished',
      text: t('enum.bookingStatus.finished'),
      onClick: () => {
        handleTrackHotelReturnStatus(BookingStatus.Finished, index)
      },
    },
  ]

  const trackHotelItemHidden = React.useCallback(
    (index: number, outward: boolean) => {
      if (outward) {
        return (
          proving.participants &&
          proving.participants.some(
            (p) =>
              p.trackHotels &&
              p.trackHotels.outward &&
              p.trackHotels.outward[index]
          )
        )
      } else {
        return (
          proving.participants &&
          proving.participants.some(
            (p) =>
              p.trackHotels &&
              p.trackHotels.return &&
              p.trackHotels.return[index]
          )
        )
      }
    },
    [proving]
  )

  useCommand(
    {
      key: 'trackHotel',
      priority: 90,
      text: t('model.proving.participant.trackHotel'),
      disabled:
        selectedItems.length === 0 || !canUseButton() || !!isMaintenanceHead,
      iconProps: { iconName: 'RealEstate' },
      hidden: !!proving.provingGround!.hideHotels,
      subMenuProps: {
        items: [
          {
            key: 'addTrackHotels',
            text: t('field.trackHotel.editTrackHotel'),
            onClick: () => {
              if (selectedItems.length > 1) {
                setNotificationState(true)
              } else {
                setNotificationState(false)
              }

              showHotelsPanel({
                trackHotels: {
                  outward:
                    selectedItems.length > 1
                      ? []
                      : selectedItems.length > 1
                      ? []
                      : selectedItems[0].trackHotels!.outward!.map(
                          (item: any) => {
                            return {
                              ...omitDeep(item, ['__typename']),
                              hotel: item.hotel ? item.hotel._id : null,
                            }
                          }
                        ),
                  return:
                    selectedItems.length > 1
                      ? []
                      : selectedItems.length > 1
                      ? []
                      : selectedItems[0].trackHotels!.return!.map(
                          (item: any) => {
                            return {
                              ...omitDeep(item, ['__typename']),
                              hotel: item.hotel ? item.hotel._id : null,
                            }
                          }
                        ),
                },
              })
              setFormState({ formType: FormType.hotels })
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'noTrackHotels',
            text: t('field.trackHotel.noTrackHotel'),
            onClick: () => {
              handleNoTrackHotels()
            },
            disabled: isExternal,
            hidden: isExternal,
          },
          {
            key: 'setStatusTrackHotelsOutward',
            text: t('field.trackHotel.setStatusOutward'),
            hidden: !isProvingResourceManager || !isExternal,
            disabled:
              !compareTrackHotels(selectedItems, 'outward') || !canUpdateStatus,
            items: [
              {
                key: 'item1',
                text: t('field.trackHotels.outward.0.label'),
                disabled: !trackHotelItemHidden(0, true),
                items: statusItemsOutward(0),
              },
              {
                key: 'item2',
                text: t('field.trackHotels.outward.1.label'),
                disabled: !trackHotelItemHidden(1, true),
                items: statusItemsOutward(1),
              },
              {
                key: 'item3',
                text: t('field.trackHotels.outward.2.label'),
                disabled: !trackHotelItemHidden(2, true),
                items: statusItemsOutward(2),
              },
              {
                key: 'item4',
                text: t('field.trackHotels.outward.3.label'),
                disabled: !trackHotelItemHidden(3, true),
                items: statusItemsOutward(3),
              },
              {
                key: 'item5',
                text: t('field.trackHotels.outward.4.label'),
                disabled: !trackHotelItemHidden(4, true),
                items: statusItemsOutward(4),
              },
              {
                key: 'item6',
                text: t('field.trackHotels.outward.5.label'),
                disabled: !trackHotelItemHidden(5, true),
                items: statusItemsOutward(5),
              },
              {
                key: 'item7',
                text: t('field.trackHotels.outward.6.label'),
                disabled: !trackHotelItemHidden(6, true),
                items: statusItemsOutward(6),
              },
              {
                key: 'item8',
                text: t('field.trackHotels.outward.7.label'),
                disabled: !trackHotelItemHidden(7, true),
                items: statusItemsOutward(7),
              },
              {
                key: 'item9',
                text: t('field.trackHotels.outward.8.label'),
                disabled: !trackHotelItemHidden(8, true),
                items: statusItemsOutward(8),
              },
              {
                key: 'item10',
                text: t('field.trackHotels.outward.9.label'),
                disabled: !trackHotelItemHidden(9, true),
                items: statusItemsOutward(9),
              },
            ],
          },
          {
            key: 'setStatusTrackHotelsReturn',
            text: t('field.trackHotel.setStatusReturn'),
            hidden: !isProvingResourceManager || !isExternal,
            disabled:
              !compareTrackHotels(selectedItems, 'return') ||
              !canUpdateStatus(),
            items: [
              {
                key: 'item1',
                text: t('field.trackHotels.return.0.label'),
                disabled: !trackHotelItemHidden(0, false),
                items: statusItemsReturn(0),
              },
              {
                key: 'item2',
                text: t('field.trackHotels.return.1.label'),
                disabled: !trackHotelItemHidden(1, false),
                items: statusItemsReturn(1),
              },
              {
                key: 'item3',
                text: t('field.trackHotels.return.2.label'),
                disabled: !trackHotelItemHidden(2, false),
                items: statusItemsReturn(2),
              },
              {
                key: 'item4',
                text: t('field.trackHotels.return.3.label'),
                disabled: !trackHotelItemHidden(3, false),
                items: statusItemsReturn(3),
              },
              {
                key: 'item5',
                text: t('field.trackHotels.return.4.label'),
                disabled: !trackHotelItemHidden(4, false),
                items: statusItemsReturn(4),
              },
              {
                key: 'item6',
                text: t('field.trackHotels.return.5.label'),
                disabled: !trackHotelItemHidden(5, false),
                items: statusItemsReturn(5),
              },
              {
                key: 'item7',
                text: t('field.trackHotels.return.6.label'),
                disabled: !trackHotelItemHidden(6, false),
                items: statusItemsReturn(6),
              },
              {
                key: 'item8',
                text: t('field.trackHotels.return.7.label'),
                disabled: !trackHotelItemHidden(7, false),
                items: statusItemsReturn(7),
              },
              {
                key: 'item9',
                text: t('field.trackHotels.return.8.label'),
                disabled: !trackHotelItemHidden(8, false),
                items: statusItemsReturn(8),
              },
              {
                key: 'item10',
                text: t('field.trackHotels.return.9.label'),
                disabled: !trackHotelItemHidden(9, false),
                items: statusItemsReturn(9),
              },
            ],
          },
        ],
      },
    },
    [selectedItems, canUpdateCurrentProving, compareTrackHotels]
  )
  //#endregion TRACKHOTEL

  useCommand(
    {
      key: 'mailing',
      priority: 100,
      text: t('model.proving.participant.mailing'),
      hidden: !userCanUseMail(),
      disabled: selectedItems.length === 0 || !userCanUseMail(),
      iconProps: { iconName: 'Mail' },
      subMenuProps: {
        items: (proving.mailTemplates || [])
          .filter((mt) => mt.inactive !== true)
          .map((x) => {
            return {
              key: x._id,
              text: x.name,
              disabled: selectedItems.length === 0,
              iconProps: { iconName: 'Mail' },
              onClick: () => {
                if (x.type === MailTemplateType.HotelBookings) {
                  const sameHotels = selectedItems.every(
                    (s) =>
                      (s.accomodation &&
                        s.accomodation.accomodation &&
                        selectedItems[0].accomodation &&
                        selectedItems[0].accomodation.accomodation &&
                        s.accomodation.accomodation._id ===
                          selectedItems[0].accomodation.accomodation._id) ||
                      false
                  )
                  if (!sameHotels) {
                    showMailingDialog()
                  } else {
                    setMailTemplateId(x._id)
                    setFormState({ formType: FormType.mailing })
                  }
                } else if (
                  x.type === MailTemplateType.TrackHotelBookingsOutbound1 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound2 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound3 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound4 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound5 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound6 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound7 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound8 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound9 ||
                  x.type === MailTemplateType.TrackHotelBookingsOutbound10
                ) {
                  const index =
                    x.type === MailTemplateType.TrackHotelBookingsOutbound1
                      ? 0
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound2
                      ? 1
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound3
                      ? 2
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound4
                      ? 3
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound5
                      ? 4
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound6
                      ? 5
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound7
                      ? 6
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound8
                      ? 7
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound9
                      ? 8
                      : x.type === MailTemplateType.TrackHotelBookingsOutbound10
                      ? 9
                      : 0

                  const sameHotels = selectedItems.every(
                    (s) =>
                      (s.trackHotels &&
                        s.trackHotels.outward &&
                        s.trackHotels.outward[index] &&
                        s.trackHotels.outward[index]!.hotel &&
                        selectedItems[0].trackHotels &&
                        selectedItems[0].trackHotels.outward &&
                        selectedItems[0].trackHotels.outward[index] &&
                        selectedItems[0].trackHotels.outward[index]!.hotel &&
                        s.trackHotels.outward[index]!.hotel!._id ===
                          selectedItems[0].trackHotels.outward[index]!.hotel!
                            ._id) ||
                      false
                  )
                  if (!sameHotels) {
                    showMailingDialog()
                  } else {
                    setMailTemplateId(x._id)
                    setFormState({ formType: FormType.mailing })
                  }
                } else if (
                  x.type === MailTemplateType.TrackHotelBookingsReturn1 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn2 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn3 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn4 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn5 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn6 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn7 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn8 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn9 ||
                  x.type === MailTemplateType.TrackHotelBookingsReturn10
                ) {
                  const index =
                    x.type === MailTemplateType.TrackHotelBookingsReturn1
                      ? 0
                      : x.type === MailTemplateType.TrackHotelBookingsReturn2
                      ? 1
                      : x.type === MailTemplateType.TrackHotelBookingsReturn3
                      ? 2
                      : x.type === MailTemplateType.TrackHotelBookingsReturn4
                      ? 3
                      : x.type === MailTemplateType.TrackHotelBookingsReturn5
                      ? 4
                      : x.type === MailTemplateType.TrackHotelBookingsReturn6
                      ? 5
                      : x.type === MailTemplateType.TrackHotelBookingsReturn7
                      ? 6
                      : x.type === MailTemplateType.TrackHotelBookingsReturn8
                      ? 7
                      : x.type === MailTemplateType.TrackHotelBookingsReturn9
                      ? 8
                      : x.type === MailTemplateType.TrackHotelBookingsReturn10
                      ? 9
                      : 0

                  const sameHotels = selectedItems.every(
                    (s) =>
                      (s.trackHotels &&
                        s.trackHotels.return &&
                        s.trackHotels.return[index] &&
                        s.trackHotels.return[index]!.hotel &&
                        selectedItems[0].trackHotels &&
                        selectedItems[0].trackHotels.return &&
                        selectedItems[0].trackHotels.return[index] &&
                        selectedItems[0].trackHotels.return[index]!.hotel &&
                        s.trackHotels.return[index]!.hotel!._id ===
                          selectedItems[0].trackHotels.return[index]!.hotel!
                            ._id) ||
                      false
                  )
                  if (!sameHotels) {
                    showMailingDialog()
                  } else {
                    setMailTemplateId(x._id)
                    setFormState({ formType: FormType.mailing })
                  }
                } else {
                  setMailTemplateId(x._id)
                  setFormState({ formType: FormType.mailing })
                }
              },
            }
          }),
      },
    },
    [selectedItems, canUpdateCurrentProving]
  )

  useGroupCommand(
    t,
    proving,
    selectedItems,
    () => {
      showAddGroupPanel({ group: selectedItems[0].group })
    },
    handleRemoveGroup,
    selectedItems.length === 0 || !canUpdateCurrentProving,
    110
  )

  useCommand(
    {
      key: 'calculateMaintenanceAreas',
      priority: 200,
      text: t('model.proving.participant.calculateMaintenanceAreas'),
      iconProps: { iconName: 'PartyLeader' },
      disabled: !canUpdateCurrentProving,
      hidden: isExternal!,
      onClick: () => {
        updateProving({
          variables: {
            id: proving.id,
            update: {
              maintenanceHead: calculateMaintenanceAreas(
                maintenanceAreaData,
                []
              ),
            },
          },
        }).then((_) => refetch())
      },
    },
    [selectedItems, maintenanceAreaData]
  )

  useCommand(
    {
      key: 'delete',
      priority: 300,
      text: t('model.proving.participant.deleteParticipant'),
      hidden:
        selectedItems.length === 0 ||
        selectedItems.filter((x: any) => x.$change).length !== 0 ||
        !!isExternal,
      disabled: !canUpdateCurrentProving || !!isMaintenanceHead || !!isExternal,
      iconProps: { iconName: 'Delete' },
      onClick: () => showDialog(),
    },
    [selectedItems, canUpdateCurrentProving]
  )

  useDeselectCommand(
    selectedItems.length,
    {
      priority: 400,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )
  // #endregion

  // #region EDITFORM

  const onItemInvoked = (item: any): void => {
    setSelectedItemId(item._id)
    setFormState({ formType: FormType.details })
    showEditPanel({
      ...item,
      flight: {
        ...item.flight,
        note: item.flight.note || '',
        outboundData: {
          ...item.flight.outboundData,
          airline: item.flight.outboundData?.airline
            ? item.flight.outboundData.airline
            : '',
          flightNumber: item.flight.outboundData?.flightNumber
            ? item.flight.outboundData.flightNumber
            : '',
        },
        returnData: {
          ...item.flight.returnData,
          airline: item.flight.returnData?.airline
            ? item.flight.returnData.airline
            : '',
          flightNumber: item.flight.returnData?.flightNumber
            ? item.flight.returnData.flightNumber
            : '',
        },
      },
      accomodation: {
        ...item.accomodation,
        accomodation: item.accomodation.accomodation
          ? item.accomodation.accomodation._id
          : '',
        note: item.accomodation.note || '',
        additionalAccomodation: item.accomodation.additionalAccomodation || '',
      },
      trackHotels: {
        outward: item.trackHotels.outward.map((out: TrackHotel) => ({
          ...out,
          hotel: out.hotel ? out.hotel._id : null,
        })),
        return: item.trackHotels.return.map((ret: TrackHotel) => ({
          ...ret,
          hotel: ret.hotel ? ret.hotel._id : null,
        })),
        ...item.trackHotel,
      },
      ferry: {
        ...item.ferry,
        outward: item.ferry.outward ? item.ferry.outward._id : null,
        return: item.ferry.return ? item.ferry.return._id : null,
        note: item.ferry.note || '',
      },
      note: item.note || '',
      rentalCar: {
        ...item.rentalCar,
        note: item.rentalCar.note || '',
      },
    })
  }

  const addIndexToTrackHotels = (batch: ParticipantBatchInput[]) => {
    batch.forEach((b) => {
      if (b.update && b.update.trackHotels && b.update.trackHotels.outward) {
        b.update.trackHotels.outward.forEach((tho, i) => (tho.index = i + 1))
      }
      if (b.update && b.update.trackHotels && b.update.trackHotels.return) {
        b.update.trackHotels.return.forEach((thr, i) => (thr.index = i + 1))
      }
    })
  }

  const handleDetailsViewSubmit = React.useCallback(
    async (values: any, actions: FormikActions<any>) => {
      actions.setSubmitting(true)

      values = clearObjectIdsByPath(values, [
        'accomodation.accomodation',
        'ferry.outward',
        'ferry.return',
      ])

      const updateValues = removeNullProperties({
        orderId: values.orderId,
        dateTo: values.dateTo,
        dateFrom: values.dateFrom,
        note: values.note,
        percentage: values.percentage,
        flight: values.flight,
        trackHotelRequired: values.trackHotelRequired,
        trackHotels: values.trackHotels,
        rentalCar: values.rentalCar,
        group: values.group,
        temporaryPass: values.temporaryPass,
        passActivation: values.passActivation,
        permanentDriveAuthorization: values.permanentDriveAuthorization,
        singleDriveAuthorization: values.singleDriveAuthorization,
        dvEquipment: values.dvEquipment,
        partsTransport: values.partsTransport,
        driverTraining: values.driverTraining,
        ferry: values.ferry,
        nordschleife: values.nordschleife,
        changeTimeStamp: dateTimeToGraphQLDateTime(new Date(Date.now())),
        changedBy: auth.user && auth.user._id,
      })
      updateValues.accomodation = values.accomodation

      const batch = [
        {
          _id: selectedItemId,
          update: omitDeep(updateValues, ['__typename']),
        },
      ]

      addIndexToTrackHotels(batch)

      const { errors } = await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      })
        .then((_) => refetch())
        .then((_) => mailChangesRefetch())

      if (errors) {
        throw errors
      }
      actions.setSubmitting(false)
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      hideEditPanel()
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
    },
    [
      auth.user,
      selectedItems,
      hideEditPanel,
      proving,
      refetch,
      mailChangesRefetch,
      updateParticipants,
      selectedItemId,
      selection,
    ]
  )
  // #endregion

  // #region
  const handleHeadOfProvingSubmit = React.useCallback(
    async (
      values: ParticipantUpdateInput,
      actions: FormikActions<ParticipantUpdateInput>
    ) => {
      actions.setSubmitting(true)

      const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
        _id: sel._id,
        update: {
          headOfProving: {
            isHeadOfProving: true,
            dateFrom: values.headOfProving!.dateFrom,
            dateTo: values.headOfProving!.dateTo,
          },
        },
      }))

      await updateParticipants({
        variables: {
          provingId: proving._id,
          batch,
        },
      }).then((_) => refetch())
      actions.setSubmitting(false)
      const selectedKeys = selectedItems.map((participant) => participant._id)
      selection.setAllSelected(false)
      selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
      hideHeadOfProvingPanel()
    },
    [
      selectedItems,
      proving,
      updateParticipants,
      hideHeadOfProvingPanel,
      refetch,
      selection,
    ]
  )

  const handleHeadOfProvingDefaultDate = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        headOfProving: {
          isHeadOfProving: true,
          dateFrom: sel.dateFrom,
          dateTo: sel.dateTo,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const handleRemoveHeadOfProving = React.useCallback(async () => {
    const batch: ParticipantBatchInput[] = selectedItems.map((sel) => ({
      _id: sel._id,
      update: {
        headOfProving: {
          isHeadOfProving: false,
          dateFrom: null,
          dateTo: null,
        },
      },
    }))

    await updateParticipants({
      variables: {
        provingId: proving._id,
        batch,
      },
    }).then((_) => refetch())
    const selectedKeys = selectedItems.map((participant) => participant._id)
    selection.setAllSelected(false)
    selectedKeys.forEach((key) => selection.setKeySelected(key, true, false))
  }, [proving, selectedItems, updateParticipants, refetch, selection])

  const validatePersonType = () => {
    if (
      selectedItems[0] &&
      selectedItems[0].person &&
      selectedItems[0].person.type === PersonType.User &&
      !(selectedItems[0].person as User).external &&
      !startsWith(selectedItems[0].person!.email!, 'extern.')
    ) {
      return false
    } else {
      return true
    }
  }

  useCommand(
    {
      key: 'headOfProving',
      priority: 100,
      text: t('model.proving.participant.headOfProving'),
      iconProps: { iconName: 'PartyLeader' },
      hidden: isExternal!,
      disabled:
        selectedItems.length !== 1 ||
        validatePersonType() ||
        !canUpdateCurrentProving ||
        !!isMaintenanceHead,
      subMenuProps: {
        items: [
          {
            key: 'addHeadofProving',
            text: t('model.proving.participant.addHeadOfProving'),
            onClick: () => {
              if (selectedItems.length > 1) {
                setNotificationState(true)
                showHeadOfProvingPanel()
              } else {
                setNotificationState(false)
                showHeadOfProvingPanel({
                  headOfProving: {
                    dateFrom: selectedItems[0].headOfProving
                      ? selectedItems[0].headOfProving.dateFrom
                      : '',
                    dateTo: selectedItems[0].headOfProving
                      ? selectedItems[0].headOfProving.dateTo
                      : '',
                  },
                })
              }
              setFormState({ formType: FormType.headOfProving })
            },
          },
          {
            key: 'addHeadOfProvingDefaultDate',
            text: t('model.proving.participant.headOfProvingDate'),
            disabled: selectedItems.length !== 1,
            onClick: () => {
              handleHeadOfProvingDefaultDate()
            },
          },
          {
            key: 'removeHeadOfProving',
            text: t('model.proving.participant.removeHeadOfProving'),
            onClick: () => {
              handleRemoveHeadOfProving()
            },
          },
        ],
      },
    },
    [selectedItems]
  )
  // #endregion

  return (
    <Page>
      <Section label={t('section.participants.annotation')}>
        <AutoStack horizontal={true}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            disabled={!canUpdateCurrentProving || !!isMaintenanceHead}
            onClick={() => {
              setFormState({ formType: FormType.annotation })
              showAnnotationPanel({
                annotation: proving.annotationParticipants || '',
              })
            }}
          />

          <RichTextViewer
            richText={
              proving.annotationParticipants
                ? proving.annotationParticipants
                : ''
            }
          />
        </AutoStack>
      </Section>
      {proving &&
        proving.maintenanceHead &&
        proving.maintenanceHead.length > 0 && (
          <Section label={t('section.proving.participant.maintenanceArea')}>
            <Flex>
              <Stack wrap={true} horizontal={true}>
                {proving.maintenanceHead.map((mh) => {
                  return (
                    <Persona
                      key={mh._id}
                      text={mh.name}
                      secondaryText={mh.department! || mh.email!}
                      size={PersonaSize.size40}
                      initialsColor={PersonaInitialsColor.lightRed}
                    />
                  )
                })}
              </Stack>
            </Flex>
          </Section>
        )}
      <Section
        label={'Teilnehmer'}
        style={{ width: 'fit-content', minWidth: '-webkit-fill-available' }}
      >
        <AppCommandBar />
        <List
          items={participants}
          provingGround={proving.provingGround}
          selection={selection}
          onItemInvoked={onItemInvoked}
          constrainMode={ConstrainMode.unconstrained}
        />
      </Section>
      <FormikPanelWizard
        {...panelState}
        onSubmit={handleAddParticipantsSubmit}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        hasCloseButton={false}
        validationSchema={new ProvingDetailsParticipantFormSchema()}
      >
        {formState !== undefined &&
          formState.formType === FormType.addParticipant &&
          getActiveWizardState(participantWizardState) ===
            WizardState.addParticipant && (
            <ProvingDetailsParticipantForm
              orderIds={proving.orderIdDistribution!.map(
                (x: OrderIdDistribution) => x.orderId
              )}
              proving={proving!}
            />
          )}
      </FormikPanelWizard>

      {/* Uncomment for timespan panel in wizard      */}
      <FormikPanelWizard
        {...timespanWizardPanelState}
        onSubmit={handleUpdateSubmit}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        hasCloseButton={false}
        onDismiss={handleTimespanWizardDismiss}
        onNextClick={handleTimespanWizardDismiss}
        validationSchema={
          new ProvingDetailsTimespanFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {getActiveWizardState(participantWizardState) ===
          WizardState.timespan && (
          <>
            <Slider
              min={1}
              max={participantWizardState.length - 2}
              step={1}
              value={getActiveWizard(participantWizardState) - 1}
              valueFormat={(value: number) =>
                `${value}/${participantWizardState.length - 2}`
              }
              showValue={true}
              disabled={true}
            />
            <ProvingDetailsTimespanForm
              proving={proving!}
              notification={notificationState}
              wizard={true}
            />
          </>
        )}
      </FormikPanelWizard>
      <FormikPanelWizard
        {...groupWizardPanelState}
        onSubmit={handleUpdateSubmit}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        hasCloseButton={false}
      >
        {getActiveWizardState(participantWizardState) === WizardState.group && (
          <>
            <Slider
              min={1}
              max={participantWizardState.length - 2}
              step={1}
              value={getActiveWizard(participantWizardState) - 1}
              valueFormat={(value: number) =>
                `${value}/${participantWizardState.length - 2}`
              }
              showValue={true}
              disabled={true}
            />
            <ProvingDetailsGroupForm
              notification={notificationState}
              wizard={true}
            />
          </>
        )}
      </FormikPanelWizard>
      <FormikPanelWizard
        {...updateWizardPanelState}
        onSubmit={handleUpdateSubmit}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        hasCloseButton={false}
      >
        {getActiveWizardState(participantWizardState) ===
          WizardState.orderId && (
          <>
            <Slider
              min={1}
              max={participantWizardState.length - 2}
              step={1}
              value={getActiveWizard(participantWizardState) - 1}
              valueFormat={(value: number) =>
                `${value}/${participantWizardState.length - 2}`
              }
              showValue={true}
              disabled={true}
            />
            <ProvingDetailsOrderIdForm
              orderIds={proving.orderIdDistribution!.map((x: any) => x)}
              notification={notificationState}
            />
          </>
        )}
        {getActiveWizardState(participantWizardState) ===
          WizardState.nordschleife && (
          <ProvingDetailsNordschleifeForm notification={notificationState} />
        )}
      </FormikPanelWizard>

      <FormikPanelWizard
        {...accomodationWizardPanelState}
        onSubmit={handleAccomodationsSubmit}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        hasCloseButton={false}
        validationSchema={new ProvingDetailsAccomodationFormSchema()}
      >
        {getActiveWizardState(participantWizardState) ===
          WizardState.accomodation && (
          <>
            <Slider
              min={1}
              max={participantWizardState.length - 2}
              step={1}
              value={getActiveWizard(participantWizardState) - 1}
              valueFormat={(value: number) =>
                `${value}/${participantWizardState.length - 2}`
              }
              showValue={true}
              disabled={true}
            />
            <ProvingDetailsAccomodationForm
              proving={proving}
              notification={notificationState}
              wizardParticipants={wizardParticipants}
            />
          </>
        )}
      </FormikPanelWizard>
      <FormikPanelWizard
        {...rentalCarWizardPanelState}
        onSubmit={handleRentalCarSubmit}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        hasCloseButton={false}
        validationSchema={
          new ProvingDetailsRentalCarFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {getActiveWizardState(participantWizardState) ===
          WizardState.rentalCar && (
          <>
            <Slider
              min={1}
              max={participantWizardState.length - 2}
              step={1}
              value={getActiveWizard(participantWizardState) - 1}
              valueFormat={(value: number) =>
                `${value}/${participantWizardState.length - 2}`
              }
              showValue={true}
              disabled={true}
            />
            <ProvingDetailsRentalCarForm
              notification={notificationState}
              proving={proving}
              wizardParticipants={wizardParticipants}
            />
          </>
        )}
      </FormikPanelWizard>
      <FormikPanelWizard
        {...flightWizardPanelState}
        onSubmit={handleWizardFlightSubmit}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        hasCloseButton={false}
        validationSchema={new ProvingDetailsFlightFormSchema()}
      >
        {getActiveWizardState(participantWizardState) ===
          WizardState.flight && (
          <>
            <Slider
              min={1}
              max={participantWizardState.length - 2}
              step={1}
              value={getActiveWizard(participantWizardState) - 1}
              valueFormat={(value: number) =>
                `${value}/${participantWizardState.length - 2}`
              }
              showValue={true}
              disabled={true}
            />
            <ProvingDetailsFlightForm
              notification={notificationState}
              proving={proving}
              wizardParticipants={wizardParticipants}
            />
          </>
        )}
      </FormikPanelWizard>
      <FormikPanelWizard
        {...hotelsWizardPanelState}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        onSubmit={handleHotelsSubmit}
        type={PanelType.extraLarge}
        hasCloseButton={false}
        validationSchema={
          new ProvingDetailsTrackHotelFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {getActiveWizardState(participantWizardState) ===
          WizardState.hotels && (
          <>
            <Slider
              min={1}
              max={participantWizardState.length - 2}
              step={1}
              value={getActiveWizard(participantWizardState) - 1}
              valueFormat={(value: number) =>
                `${value}/${participantWizardState.length - 2}`
              }
              showValue={true}
              disabled={true}
            />
            <ProvingDetailsTrackHotelForm
              notification={notificationState}
              proving={proving}
            />
          </>
        )}
      </FormikPanelWizard>
      {/* <FormikPanelWizard
        {...ferryWizardPanelState}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        onSubmit={handleFerrySubmit}
        hasCloseButton={false}
        validationSchema={
          new ProvingDetailsFerryFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {getActiveWizardState(participantWizardState) === WizardState.ferry && (
          <ProvingDetailsFerryForm
            proving={proving}
            notification={notificationState}
          />
        )}
      </FormikPanelWizard> */}
      <FormikPanelWizard
        {...unkownParticipantPanelState}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        hasCloseButton={false}
        onSubmit={handleUnkownParticipantSubmit}
      >
        {formState !== undefined &&
          formState.formType === FormType.addParticipant && (
            <ProvingDetailsParticipantsUnknownForm />
          )}
      </FormikPanelWizard>
      <FormikPanelWizard
        {...workerPanelState}
        onSubmit={handleWorkerSubmit}
        hasCloseButton={false}
        handleWizardChange={navigationChangeHandler}
        wizardState={participantWizardState}
        validationSchema={new ProvingDetailsWorkerFormSchema()}
      >
        {getActiveWizardState(participantWizardState) ===
          WizardState.addParticipant && (
          <ProvingDetailsWorkerForm
            orderIds={proving.orderIdDistribution!.map((x: any) => x.orderId!)}
          />
        )}
      </FormikPanelWizard>
      <FormikPanel {...annotationPanelState} onSubmit={handleAnnotationSubmit}>
        {formState !== undefined &&
          formState.formType === FormType.annotation && (
            <ProvingDetailsAnnotationForm />
          )}
      </FormikPanel>
      <FormikPanel
        {...editPanelState}
        onSubmit={handleDetailsViewSubmit}
        type={PanelType.large}
        validationSchema={
          new ParticipantDetailsFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {formState !== undefined && formState.formType === FormType.details && (
          <ParticipantDetailsForm />
        )}
      </FormikPanel>
      <FormikPanel
        {...timespanPanelState}
        onSubmit={handleTimespanSubmit}
        validationSchema={
          new ProvingDetailsTimespanFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {formState !== undefined &&
          formState.formType === FormType.timespan && (
            <ProvingDetailsTimespanForm
              proving={proving!}
              notification={notificationState}
              changeNotification={true}
            />
          )}
      </FormikPanel>
      <FormikPanel
        {...headOfProvingPanelState}
        onSubmit={handleHeadOfProvingSubmit}
      >
        {formState !== undefined &&
          formState.formType === FormType.headOfProving && (
            <ParticipantHeadOfProvingForm
              proving={proving!}
              notification={notificationState}
              selection={selectedItems[0]}
            />
          )}
      </FormikPanel>
      <FormikPanel
        {...rentalCarPanelState}
        onSubmit={handleRentalCarSubmit}
        validationSchema={
          new ProvingDetailsRentalCarFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {formState !== undefined &&
          formState.formType === FormType.rentalCar && (
            <ProvingDetailsRentalCarForm
              notification={notificationState}
              proving={proving}
            />
          )}
      </FormikPanel>
      <FormikPanel
        {...accomodationPanelState}
        onSubmit={handleAccomodationsSubmit}
        validationSchema={new ProvingDetailsAccomodationFormSchema()}
      >
        {formState !== undefined &&
          formState.formType === FormType.accomodation && (
            <ProvingDetailsAccomodationForm
              proving={proving}
              notification={notificationState}
            />
          )}
      </FormikPanel>
      <FormikPanel
        {...hotelsPanelState}
        onSubmit={handleHotelsSubmit}
        type={PanelType.extraLarge}
        validationSchema={
          new ProvingDetailsTrackHotelFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {formState !== undefined && formState.formType === FormType.hotels && (
          <ProvingDetailsTrackHotelForm
            notification={notificationState}
            proving={proving}
          />
        )}
      </FormikPanel>
      <FormikPanel
        {...hotelsOutwardStatusPanelState}
        onSubmit={handleUpdateSubmit}
        type={PanelType.extraLarge}
      >
        {formState !== undefined &&
          formState.formType === FormType.hotelsOutwardStatus && (
            <ProvingDetailsTrackHotelOutwardStatusForm proving={proving} />
          )}
      </FormikPanel>
      <FormikPanel
        {...hotelsReturnStatusPanelState}
        onSubmit={handleUpdateSubmit}
        type={PanelType.extraLarge}
      >
        {formState !== undefined &&
          formState.formType === FormType.hotelsReturnStatus && (
            <ProvingDetailsTrackHotelReturnStatusForm proving={proving} />
          )}
      </FormikPanel>
      <FormikPanel
        {...updatePanelState}
        onSubmit={handleUpdateSubmit}
        type={PanelType.largeFixed}
      >
        {formState !== undefined && formState.formType === FormType.orderId && (
          <ProvingDetailsOrderIdForm
            orderIds={proving.orderIdDistribution!.map((x: any) => x)}
            notification={notificationState}
          />
        )}

        {formState !== undefined &&
          formState.formType === FormType.nordschleife && (
            <ProvingDetailsNordschleifeForm notification={notificationState} />
          )}
      </FormikPanel>
      <FormikPanel
        {...ferryPanelState}
        onSubmit={handleFerrySubmit}
        validationSchema={
          new ProvingDetailsFerryFormSchema(
            proving.dateFrom as string,
            proving.dateTo as string
          )
        }
      >
        {formState !== undefined && formState.formType === FormType.ferry && (
          <ProvingDetailsFerryForm
            proving={proving}
            notification={notificationState}
          />
        )}
      </FormikPanel>

      <FormikPanel
        {...supplierPanelState}
        type={PanelType.large}
        onSubmit={handleSupplierSubmit}
        validationSchema={new ProvingDetailsSupplierFormSchema()}
      >
        <ProvingDetailsSupplierForm
          orderIds={proving.orderIdDistribution!.map((x: any) => x.orderId!)}
        />
      </FormikPanel>
      <FormikPanel
        {...participantManuellPanelState}
        onSubmit={handleManuellSubmit}
      >
        <ProvingDetailsManuellParticipantForm
          orderIds={proving.orderIdDistribution!.map((x: any) => x.orderId!)}
        />
      </FormikPanel>
      <FormikPanel {...reassignPanelState} onSubmit={handleReassignSubmit}>
        <ProvingDetailsParticipantReassignForm
          participant={selectedItems[0] || {}}
        />
      </FormikPanel>
      <FormikPanel
        {...participantFromProvingPanelState}
        validationSchema={ParticipantsByProvingFormSchema}
        type={PanelType.extraLarge}
        onSubmit={handleParticiantsFromProvingSubmit}
      >
        <ParticipantsByProvingForm />
      </FormikPanel>
      <FormikPanel
        {...addGroupPanelState}
        onSubmit={handleGroupAssign}
        headerText={'Gruppe zuordnen'}
      >
        <ProvingDetailsGroupForm />
      </FormikPanel>
      {formState !== undefined && formState.formType === FormType.mailing && (
        <FlexSpinner loading={mailChangesLoading}>
          <MailboxForm
            loadNewMails={false}
            location="participants"
            changes={changes as any}
            isOpen={formState.formType === FormType.mailing}
            mode={MailboxFormMode.New}
            onDismiss={() => {
              setFormState({ formType: FormType.default })
            }}
            onSaved={() => {
              setFormState({ formType: FormType.default })
            }}
            proving={proving}
            templateId={mailTemplateId}
            context={selectedItems}
          />
        </FlexSpinner>
      )}
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
      <MailingDialog
        titleText="Bitte wählen Sie nur Personen mit dem gleichen Hotel aus!"
        {...mailingDialogState}
      />
      {/* <Section label={t('section.debug.header')}>
        <pre>{JSON.stringify(proving, null, 2)}</pre>
      </Section> */}
    </Page>
  )
}

function compareTrackHotels(
  selectedItems: Participant[],
  direction: 'outward' | 'return'
): boolean {
  const [firstItem, ...restItems] = selectedItems
  const firstDirection = firstItem?.trackHotels?.[direction]

  if (!firstDirection) {
    return false
  }

  return restItems.every((item) => {
    const directionItems = item?.trackHotels?.[direction] ?? []

    if (directionItems.length === 0) {
      return false
    }

    return directionItems.every((to, i) =>
      isEqual(
        omit(to, ['_id', 'bookingStatus']),
        omit(firstDirection[i], ['_id', 'bookingStatus'])
      )
    )
  })
}

export const PageParticipantIndexView: React.FC = () => <PageParticipants />
